<template>
  <div class="theme-1 min-h-[calc(100vh-200px)] py-12 px-4 md:px-8 max-w-5xl mx-auto">
    <div class="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
      <div class="p-8 md:p-12 border-b border-slate-50 bg-slate-50/30">
        <h2 class="text-3xl font-extrabold text-slate-800 tracking-tight mb-2">Complete seu Cadastro</h2>
        <p class="text-slate-500 font-medium">Preencha as informações abaixo para criar sua presença online.</p>
      </div>
      
      <form class="p-8 md:p-12 space-y-8" @submit.prevent="nextStep">
        <fieldset>
          <legend>Dados do Responsável</legend>
          <div class="field">
            <label for="tipoPessoa">Tipo de Pessoa</label>
            <div class="grid grid-cols-2 gap-4">
              <label class="flex items-center justify-center p-4 border border-slate-200 rounded-2xl cursor-pointer hover:bg-orange-50/50 hover:border-orange-200 transition-all" :class="{'bg-orange-50 border-orange-200 ring-2 ring-orange-500/10': form.tipoPessoa === 'Física'}">
                <input type="radio" id="pessoaFisica" value="Física" v-model="form.tipoPessoa" class="sr-only" />
                <span class="font-bold" :class="form.tipoPessoa === 'Física' ? 'text-orange-600' : 'text-slate-600'">Pessoa Física</span>
              </label>
              <label class="flex items-center justify-center p-4 border border-slate-200 rounded-2xl cursor-pointer hover:bg-orange-50/50 hover:border-orange-200 transition-all" :class="{'bg-orange-50 border-orange-200 ring-2 ring-orange-500/10': form.tipoPessoa === 'Jurídica'}">
                <input type="radio" id="pessoaJuridica" value="Jurídica" v-model="form.tipoPessoa" class="sr-only" />
                <span class="font-bold" :class="form.tipoPessoa === 'Jurídica' ? 'text-orange-600' : 'text-slate-600'">Pessoa Jurídica</span>
              </label>
            </div>
          </div>

          <div v-if="form.tipoPessoa === 'Jurídica'" class="space-y-6">
            <div class="field">
              <label for="razaoSocial">Razão Social*</label>
              <input id="razaoSocial" v-model="form.razaoSocial" placeholder="Nome da empresa" required />
            </div>
            <div class="field">
              <label for="cnpj">CNPJ*</label>
              <input id="cnpj" @keyup="formatCNPJ" v-model="form.cnpj" placeholder="00.000.000/0000-00" required />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="field">
              <label for="nomeResponsavel">Nome do Responsável*</label>
              <input id="nomeResponsavel" v-model="form.nomeResponsavel" placeholder="Seu nome completo" required />
            </div>
            <div class="field">
              <label for="cpfResponsavel">CPF do Responsável*</label>
              <input id="cpfResponsavel" @keyup="formatCPF" v-model="form.cpfResponsavel" placeholder="000.000.000-00" required />
            </div>
          </div>

          <div class="field">
            <label for="email">E-mail Corporativo*</label>
            <input id="email" :disabled="lockEmail" type="email" v-model="form.email" placeholder="nome@exemplo.com" required />
          </div>

          <div class="field bg-orange-50/80 backdrop-blur-sm p-6 rounded-2xl border border-orange-100" v-if="exists && !user.email">
            <div class="mb-4">
              <h4 class="text-orange-800 font-bold flex items-center gap-2">
                <span>Esta conta já existe</span>
              </h4>
              <p class="text-orange-700/80 text-sm">Por favor, faça login para continuar o cadastro.</p>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">            
              <div v-if="adminLogin">            
                <label for="user_email" class="text-xs font-bold text-orange-800 mb-1 block uppercase">Usuário</label>
                <input id="user_email" type="text" v-model="form.user_email" class="!py-2" />
              </div>
              <div>
                <label for="user_pass" class="text-xs font-bold text-orange-800 mb-1 block uppercase">Senha</label>
                <input id="user_pass" type="password" v-model="form.user_pass" class="!py-2" />
              </div>
              <button type="button" @click="login" class="!py-2 shadow-none hover:translate-y-0">Entrar</button>
            </div>
            
            <div class="mt-4 flex justify-between items-center text-xs">
              <span class="cursor-pointer text-orange-600 font-bold hover:underline" @click="adminLogin=true" v-if="!adminLogin">Entrar como admin</span>
              <span class="cursor-pointer text-orange-600 font-bold hover:underline" @click="adminLogin=false" v-if="adminLogin">Entrar como usuário</span>
            </div>
          </div>

          <div class="field">
            <label for="telefone">WhatsApp / Telefone*</label>
            <input id="telefone" @keyup="formatTelefone" v-model="form.telefone" placeholder="(00) 00000-0000" required />
          </div>
        </fieldset>

        <fieldset>
          <legend>Onde você está localizado?</legend>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="field">
              <label for="cep">CEP*</label>
              <input id="cep" v-model="form.cep" @keyup="loadCEP" placeholder="00000-000" required />
            </div>
            <div class="field md:col-span-2">
              <label for="endereco">Logradouro*</label>
              <input id="endereco" v-model="form.endereco" placeholder="Rua, Avenida, etc." required />
            </div>
          </div>
          
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div class="field">
              <label for="numero">Número*</label>
              <input id="numero" v-model="form.numero" placeholder="123" required />
            </div>
            <div class="field md:col-span-2">
              <label for="bairro">Bairro*</label>
              <input id="bairro" v-model="form.bairro" placeholder="Centro" required />
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div class="field">
              <label for="cidade">Cidade*</label>
              <input id="cidade" v-model="form.cidade" readonly class="!bg-slate-50 cursor-default" required />
            </div>
            <div class="field">
              <label for="estado">Estado*</label>
              <input id="estado" v-model="form.estado" readonly class="!bg-slate-50 cursor-default" required />
            </div>
          </div>
        </fieldset>

        <div class="pt-8 text-right">
          <Float>
            <button type="submit" class="w-full md:w-auto min-w-[200px]">Próximo passo</button>
          </Float>
        </div>
      </form>
    </div>
  </div>
</template>
<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useRegisterStore } from '@/store/registerStore';
import { useApiFetch } from '@/composables/useApiFetch';
import Float from '@/components/Float.vue';
const router = useRouter();
const registerStore = useRegisterStore();
const form = reactive(registerStore.formData);
const exists = ref(false)
const adminLogin = ref(false)
const user = computed(() => registerStore.user, { deep: true, immediate: true });
const lockEmail = computed(() => {
  return !registerStore.isAdmin && user?.email;
});
watch(() => form, (newForm) => {
  console.log('Form mudou:', newForm);
  registerStore.setFormData(newForm);
}, { deep: true });
const login = () => {
  // Aqui você pode adicionar a lógica para enviar os dados ou navegar para o próximo passo
  console.log('Dados do formulário:', form);
  // Por exemplo, redirecionar para a próxima etapa
  const formData = new FormData();
  formData.append('user_email', form.user_email || '');
  formData.append('user_pass', form.user_pass || '');
  formData.append('email', form.email || '');
  formData.append('admin', adminLogin.value ? 1 : 0);
  useApiFetch('/registro/login', {
    method: 'POST',
    body: formData,
  })
    .then(res => {
      registerStore.setToken(res.token);
      registerStore.setIsAdmin(adminLogin.value ? 1 : 0);
      registerStore.setUser(res.user);
      user.value = res.user
      if(!adminLogin.value) {
        router.push({ name: 'site' });
      }
    })
    .catch(error => {
      console.error('Erro ao enviar dados:', error);
    });
};
const nextStep = () => {
  // Se já está autenticado, o e-mail já foi validado no login — não faz sentido
  // bloquear por "E-mail já existe". Considera autenticado quando há token de
  // registro OU quando o usuário logado (badge) é o mesmo e-mail do formulário.
  const loggedEmail = (registerStore.user?.email || '').toLowerCase().trim();
  const formEmail = (form.email || '').toLowerCase().trim();
  if(registerStore.token || (loggedEmail && loggedEmail === formEmail)) {
    router.push({ name: 'site' });
    return;
  }
  // Aqui você pode adicionar a lógica para enviar os dados ou navegar para o próximo passo
  console.log('Dados do formulário:', form);
  // Por exemplo, redirecionar para a próxima etapa
  const formData = new FormData();
  for (const key in form) {
    formData.append(key, form[key]);
  }
  useApiFetch('/registro/verify', {
    method: 'POST',
    body: formData,
  })
    .then(() => {
      router.push({ name: 'site' });
    })
    .catch(error => {
      console.error('Erro ao enviar dados:', error);
      exists.value = true;
    });
};
sessionStorage.setItem('processing', 0);

const formatCNPJ = (event) => {
  let value = event.target.value.replace(/\D/g, '');
  value = value.slice(0, 14);
  value = value.replace(/^(\d{2})(\d{1,3})$/, '$1.$2');
  value = value.replace(/^(\d{2})(\d{3})(\d{1,3})$/, '$1.$2.$3');
  value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{1,4})$/, '$1.$2.$3/$4');
  value = value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})$/, '$1.$2.$3/$4-$5');
  event.target.value = value;
};
const formatCPF = (event) => {
  let value = event.target.value.replace(/\D/g, '');
  value = value.slice(0, 11);
  value = value.replace(/^(\d{3})(\d{1,3})$/, '$1.$2');
  value = value.replace(/^(\d{3})(\d{3})(\d{1,3})$/, '$1.$2.$3');
  value = value.replace(/^(\d{3})(\d{3})(\d{3})(\d{1,2})$/, '$1.$2.$3-$4');
  event.target.value = value;
};
const formatTelefone = (event) => {
  let value = event.target.value.replace(/\D/g, '');
  value = value.slice(0, 11);
  value = value.replace(/^(\d{1,2})$/, '($1)');
  value = value.replace(/^(\d{2})(\d{1,5})$/, '($1) $2');
  value = value.replace(/^(\d{2})(\d{4})(\d{1,4})$/, '($1) $2-$3');
  value = value.replace(/^(\d{2})(\d{5})(\d{1,4})$/, '($1) $2-$3');
  event.target.value = value;
};
const loadCEP = async (event) => {
  const cep = event.target.value.replace(/\D/g, '');
  if (cep.length === 8) {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        form.estado = data.uf;
        form.cidade = data.localidade;
        form.bairro = data.bairro;
        form.endereco = data.logradouro;
      } else {
        alert('CEP não encontrado.');
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    }
  }
};
onMounted(() => {
if(user.value.email) {
  // Se já estiver logado, apenas redireciona
  router.push({ name: 'site' });
}
});
</script>